import { observable, computed, makeAutoObservable } from 'mobx';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';
import { reduxImmutableHash } from './utils/reduxImmutableHash';
import { ReduxRawState } from './utils/ReduxRawState';
import { ReportDownloadOptionsUnion } from 'src/AppState/ConfigState/types';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';

const ReportsDataIO = t.interface({
    collections: t.interface({
        reports: t.interface({
            accountId: t.union([t.undefined, t.null, t.string]),
            items: t.array(t.string),
            state: t.string,
        }),
    }),
    items: t.record(
        t.string,
        t.interface({
            createdAt: t.string,
            id: t.string,
            metadata: t.interface({
                startDate: t.union([t.undefined, t.null, t.string]),
                endDate: t.union([t.undefined, t.null, t.string]),
                lastBetAfter: t.union([t.undefined, t.null, t.string]),
                expiresAfter: t.union([t.undefined, t.null, t.string]),
                expiresBefore: t.union([t.undefined, t.null, t.string]),
                from: t.union([t.undefined, t.null, t.string]),
                to: t.union([t.undefined, t.null, t.string]),
            }),
            status: t.string,
            type: t.string,
            versions: t.interface({
                csv: t.union([
                    t.undefined,
                    t.null,
                    t.interface({
                        url: t.string,
                    }),
                ]),
                html: t.union([
                    t.undefined,
                    t.null,
                    t.interface({
                        url: t.string,
                    }),
                ]),
                pdf: t.union([
                    t.undefined,
                    t.null,
                    t.interface({
                        url: t.string,
                    }),
                ]),
            }),
            template: t.union([t.undefined, t.null, t.string]),
        })
    ),
});

export type ReportsDataType = t.TypeOf<typeof ReportsDataIO>;

const isReports = createGuard(ReportsDataIO);

export class ReduxReportState {
    @observable public reports: ReportsDataType | null = null;
    @observable public viewerContent: string | null = null;

    public constructor(
        reduxStore: ReduxRawState,
        private readonly configState: ConfigState
    ) {
        reduxImmutableHash(
            reduxStore,
            (state: Record<string, unknown>): unknown => state.reports,
            (data: unknown): void => {
                if (isReports(data)) {
                    this.reports = data;
                } else {
                    console.log('error decoding report data', data);
                }
            }
        );
        makeAutoObservable(this);
    }

    @computed public get reportsDownloadOptions(): Array<ReportDownloadOptionsUnion> {
        return this.configState.config.reportDownloadOptions;
    }

    @computed public get reportsListId(): Array<string> {
        return this.reports?.collections.reports.items ?? [];
    }

    @computed public get isLoading(): boolean {
        return this.reports?.collections.reports.state === 'IN_PROGRESS';
    }

    public openViewer(url: string | undefined): void {
        if (url !== undefined) {
            this.viewerContent = url;
        }
    }

    public closeViewer = (): void => {
        this.viewerContent = null;
    };
}
